/**
 * Point Cloud Eraser Utility Functions
 * Helper functions for erasing/hiding points in point clouds
 */

const THREE = window.THREE;
import { warn as debugWarn } from '@/utils/debugLogger';

/**
 * Class to manage erased volumes (shader-based approach)
 */
export class EraserState {
    constructor() {
        // Array of THREE.Box3 representing erased volumes
        this.erasedVolumes = [];

        // History for undo/redo
        this.undoStack = [];
        this.redoStack = [];
        this.maxHistorySize = 50;
        this.maxVolumes = 16; // Shader uniform array size limit
    }

    /**
     * Add an erased volume
     * @param {THREE.Box3} box - Bounding box of erased region
     * @returns {boolean} Success flag
     */
    addVolume(box) {
        if (this.erasedVolumes.length >= this.maxVolumes) {
            debugWarn('EraserState', 'Max erased volumes reached:', this.maxVolumes);
            return false;
        }

        this.erasedVolumes.push(box.clone());
        return true;
    }

    /**
     * Get transformation matrices for shader uniforms
     * @returns {Array<THREE.Matrix4>} Array of inverted transformation matrices
     */
    getVolumeMatrices() {
        return this.erasedVolumes.map(box => {
            const matrix = new THREE.Matrix4();
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());

            // Create transform: translate to center, then scale to size
            matrix.makeTranslation(center.x, center.y, center.z);
            const scaleMatrix = new THREE.Matrix4().makeScale(size.x, size.y, size.z);
            matrix.multiply(scaleMatrix);

            // Invert so shader can check if point is inside (-0.5 to 0.5 range)
            return matrix.invert();
        });
    }

    /**
     * Get total count of erased volumes
     */
    getTotalHiddenCount() {
        return this.erasedVolumes.length;
    }

    /**
     * Clear all erased volumes
     */
    clearAll() {
        this.erasedVolumes = [];
    }

    /**
     * Clone current state for history
     */
    cloneState() {
        return this.erasedVolumes.map(box => box.clone());
    }

    /**
     * Restore state from clone
     */
    restoreState(clonedState) {
        this.erasedVolumes = clonedState.map(box => box.clone());
    }

    /**
     * Push current state to undo stack (before making changes)
     */
    pushToHistory() {
        const state = this.cloneState();
        this.undoStack.push(state);

        // Limit history size
        if (this.undoStack.length > this.maxHistorySize) {
            this.undoStack.shift();
        }

        // Clear redo stack when new action is performed
        this.redoStack = [];
    }

    /**
     * Undo last eraser action
     */
    undo() {
        if (this.undoStack.length === 0) return false;

        // Save current state to redo stack
        const currentState = this.cloneState();
        this.redoStack.push(currentState);

        // Restore previous state
        const previousState = this.undoStack.pop();
        this.restoreState(previousState);

        return true;
    }

    /**
     * Redo last undone action
     */
    redo() {
        if (this.redoStack.length === 0) return false;

        // Save current state to undo stack
        const currentState = this.cloneState();
        this.undoStack.push(currentState);

        // Restore redo state
        const redoState = this.redoStack.pop();
        this.restoreState(redoState);

        return true;
    }

    /**
     * Export state for persistence
     */
    exportState() {
        return {
            volumes: this.erasedVolumes.map(box => ({
                min: { x: box.min.x, y: box.min.y, z: box.min.z },
                max: { x: box.max.x, y: box.max.y, z: box.max.z }
            }))
        };
    }

    /**
     * Import state from exported data
     */
    importState(exported) {
        this.erasedVolumes = [];
        if (exported && exported.volumes) {
            exported.volumes.forEach(vol => {
                const box = new THREE.Box3(
                    new THREE.Vector3(vol.min.x, vol.min.y, vol.min.z),
                    new THREE.Vector3(vol.max.x, vol.max.y, vol.max.z)
                );
                this.erasedVolumes.push(box);
            });
        }
    }
}

/**
 * Convert screen rectangle to normalized device coordinates
 */
export function screenRectToNDC(rect, width, height) {
    return {
        minX: (rect.minX / width) * 2 - 1,
        maxX: (rect.maxX / width) * 2 - 1,
        minY: -((rect.minY / height) * 2 - 1),
        maxY: -((rect.maxY / height) * 2 - 1)
    };
}

/**
 * Check if a point in NDC is inside a rectangle in NDC
 */
export function isPointInNDCRect(ndcPoint, ndcRect) {
    return ndcPoint.x >= Math.min(ndcRect.minX, ndcRect.maxX) &&
           ndcPoint.x <= Math.max(ndcRect.minX, ndcRect.maxX) &&
           ndcPoint.y >= Math.min(ndcRect.minY, ndcRect.maxY) &&
           ndcPoint.y <= Math.max(ndcRect.minY, ndcRect.maxY);
}

/**
 * Get mouse position relative to canvas
 */
export function getMousePosition(event, canvas) {
    const rect = canvas.getBoundingClientRect();
    return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
    };
}

/**
 * Create selection rectangle from two points
 */
export function createSelectionRect(start, end) {
    return {
        minX: Math.min(start.x, end.x),
        minY: Math.min(start.y, end.y),
        maxX: Math.max(start.x, end.x),
        maxY: Math.max(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y)
    };
}

/**
 * Create ray from screen coordinates through camera
 * @param {Object} pointer - {clientX, clientY} screen coordinates
 * @param {Object} viewer - Forge Viewer instance
 * @returns {THREE.Ray} Ray from camera through pointer
 */
export function pointerToRay(pointer, viewer) {
    const camera = viewer.navigation.getCamera();
    const domElement = viewer.canvas;
    const rect = domElement.getBoundingClientRect();

    const x = ((pointer.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((pointer.clientY - rect.top) / rect.height) * 2 + 1;

    const pointerVector = new THREE.Vector3();
    const pointerDir = new THREE.Vector3();
    const rayCaster = new THREE.Raycaster();

    if (camera.isPerspective) {
        // Perspective camera
        pointerVector.set(x, y, 0.5);
        pointerVector.unproject(camera);

        rayCaster.set(
            camera.position,
            pointerVector.sub(camera.position).normalize()
        );
    } else {
        // Orthographic camera
        pointerVector.set(x, y, -15);
        pointerVector.unproject(camera);

        pointerDir.set(0, 0, -1);
        rayCaster.set(
            pointerVector,
            pointerDir.transformDirection(camera.matrixWorld)
        );
    }

    return rayCaster.ray;
}

/**
 * Intersect ray with axis-aligned bounding box
 * @param {THREE.Ray} ray - Ray to test
 * @param {THREE.Box3} box - Bounding box
 * @returns {Object|null} {entry, exit} points or null if no intersection
 */
export function intersectRayWithBox(ray, box) {
    // Avoid division by zero
    const invDir = new THREE.Vector3(
        1.0 / (ray.direction.x || 1e-10),
        1.0 / (ray.direction.y || 1e-10),
        1.0 / (ray.direction.z || 1e-10)
    );

    // Calculate intersection distances for each axis
    let t1 = (box.min.x - ray.origin.x) * invDir.x;
    let t2 = (box.max.x - ray.origin.x) * invDir.x;
    let tmin = Math.min(t1, t2);
    let tmax = Math.max(t1, t2);

    t1 = (box.min.y - ray.origin.y) * invDir.y;
    t2 = (box.max.y - ray.origin.y) * invDir.y;
    tmin = Math.max(tmin, Math.min(t1, t2));
    tmax = Math.min(tmax, Math.max(t1, t2));

    t1 = (box.min.z - ray.origin.z) * invDir.z;
    t2 = (box.max.z - ray.origin.z) * invDir.z;
    tmin = Math.max(tmin, Math.min(t1, t2));
    tmax = Math.min(tmax, Math.max(t1, t2));

    // Check if ray intersects box
    if (tmax < tmin || tmax < 0) {
        return null; // No intersection
    }

    // Calculate entry and exit points
    const entry = ray.at(Math.max(0, tmin), new THREE.Vector3());
    const exit = ray.at(tmax, new THREE.Vector3());

    return { entry, exit };
}

/**
 * Convert screen rectangle to 3D frustum via ray-box intersection
 * @param {Object} rect - Screen rectangle
 * @param {Object} viewer - Forge Viewer instance
 * @param {THREE.Box3} cropBox - Cropbox to intersect with
 * @returns {Object} Frustum with cornersNear and cornersFar arrays
 */
export function screenRectToFrustum(rect, viewer, cropBox) {
    // Get canvas position in viewport to convert canvas-relative to client-absolute coords
    const canvasRect = viewer.canvas.getBoundingClientRect();

    // Get 4 corners of rectangle
    // Note: rect has canvas-relative coords, but pointerToRay expects client-absolute
    const corners2D = [
        { clientX: rect.minX + canvasRect.left, clientY: rect.minY + canvasRect.top }, // Top-left
        { clientX: rect.maxX + canvasRect.left, clientY: rect.minY + canvasRect.top }, // Top-right
        { clientX: rect.maxX + canvasRect.left, clientY: rect.maxY + canvasRect.top }, // Bottom-right
        { clientX: rect.minX + canvasRect.left, clientY: rect.maxY + canvasRect.top }  // Bottom-left
    ];

    const cornersNear = [];
    const cornersFar = [];

    // Cast ray through each corner and intersect with cropbox
    corners2D.forEach(corner => {
        const ray = pointerToRay(corner, viewer);
        const intersection = intersectRayWithBox(ray, cropBox);

        if (intersection) {
            cornersNear.push(intersection.entry);
            cornersFar.push(intersection.exit);
        }
    });

    return { cornersNear, cornersFar };
}

